import { isValidCpf, maskCPF } from './formatters';
import { SIGNERS } from '../config/company';

/**
 * SIGNATÁRIOS DE UM DOCUMENTO PARA ASSINATURA.
 *
 * O mesmo `digital_contracts` serve a dois fluxos — venda nova (lead →
 * proposta) e termo aditivo (contrato) — e os dois assinam pela ClickSign com
 * as mesmas regras. Isto mora fora dos componentes para não haver duas
 * validações: uma que deixa passar e outra que barra o mesmo signatário.
 *
 * ⛔ LER O QUE ESTÁ GRAVADO NÃO É DETALHE. No fluxo do lead a lista nasce vazia
 * e o vendedor preenche. No aditivo, não: o #54 do Sicoob já tem três
 * signatários gravados com CPF, conferidos por gente. Abrir vazio faria alguém
 * redigitar — e redigitar CPF de terceiro é onde o erro entra.
 */

export type SignerRole = 'sign' | 'witness';
export type SignerAuthType = 'email' | 'icp_brasil' | 'whatsapp';

export interface SignerForm {
  name: string;
  email: string;
  cpf: string;
  birthday: string;
  phone: string;
  role: SignerRole;
  authType: SignerAuthType;
  _expanded?: boolean;
}

/**
 * Quem assina pela IPCOM. Nome, e-mail, CPF e nascimento vêm de
 * `config/company`, que é o cadastro — aqui só se escolhe o PAPEL e o MÉTODO,
 * que são decisão de cada um e não mudam por documento.
 */
export const IPCOM_SIGNERS: Record<'heitor' | 'fabio', Omit<SignerForm, '_expanded'>> = {
  heitor: {
    name: SIGNERS.heitor.name, email: SIGNERS.heitor.email, cpf: SIGNERS.heitor.cpf,
    birthday: SIGNERS.heitor.birthday, phone: SIGNERS.heitor.phone,
    role: 'witness', authType: 'whatsapp',
  },
  fabio: {
    name: SIGNERS.fabio.name, email: SIGNERS.fabio.email, cpf: SIGNERS.fabio.cpf,
    birthday: SIGNERS.fabio.birthday, phone: SIGNERS.fabio.phone,
    role: 'sign', authType: 'icp_brasil',
  },
};

export const signatarioVazio = (): SignerForm => ({
  name: '', email: '', cpf: '', birthday: '', phone: '', role: 'sign', authType: 'email', _expanded: true,
});

/**
 * O que está gravado em `digital_contracts.signers` vira formulário.
 *
 * Tolerante de propósito: o registro veio de épocas diferentes do sistema e há
 * linha sem `authType`, sem `role` e com CPF sem máscara. Perder um signatário
 * por causa de campo ausente seria pior que exibi-lo incompleto — quem confere
 * é gente, e ela vê o que falta.
 */
export function signersGravados(raw: any): SignerForm[] {
  const lista: any[] = Array.isArray(raw) ? raw : [];
  return lista
    .filter((s) => s && (s.name || s.email))
    .map((s) => ({
      name: String(s.name ?? ''),
      email: String(s.email ?? ''),
      cpf: s.cpf ? maskCPF(String(s.cpf)) : '',
      birthday: String(s.birthday ?? '').slice(0, 10),
      phone: String(s.phone ?? ''),
      role: s.role === 'witness' ? 'witness' : 'sign',
      authType: s.authType === 'icp_brasil' || s.authType === 'whatsapp' ? s.authType : 'email',
      _expanded: false,
    }));
}

/** O corpo que vai no `send-for-signature`. Campo vazio some em vez de ir "". */
export function payloadDeSigners(signers: SignerForm[]) {
  return (signers || []).map((s) => ({
    name: s.name,
    email: s.email,
    cpf: s.cpf || undefined,
    birthday: s.birthday || undefined,
    phone: s.phone || undefined,
    role: s.role,
    authType: s.authType || 'email',
  }));
}

/**
 * O que impede o envio, em português, uma linha por problema.
 *
 * Nome completo é exigência da ClickSign, não capricho: ela recusa cadastro com
 * nome único e a mensagem que volta de lá não diz qual signatário é.
 */
export function errosDosSignatarios(signers: SignerForm[]): string[] {
  const erros: string[] = [];
  const lista = signers || [];
  if (!lista.length) return ['Adicione ao menos um signatário.'];
  lista.forEach((s, i) => {
    const num = i + 1;
    if (!s.name) erros.push(`Signatário ${num}: Nome é obrigatório`);
    else if (!s.name.trim().includes(' ')) {
      erros.push(`Signatário ${num}: Informe o nome completo (nome e sobrenome). Ex: "João Silva"`);
    }
    if (!s.email) erros.push(`Signatário ${num}: Email é obrigatório`);
    if (s.cpf && !isValidCpf(s.cpf)) erros.push(`Signatário ${num}: CPF inválido`);
    if (s.authType === 'icp_brasil' && !isValidCpf(s.cpf)) {
      erros.push(`Signatário ${num}: CPF é obrigatório para Certificado Digital`);
    }
    if (s.authType === 'whatsapp' && !s.phone) {
      erros.push(`Signatário ${num}: Telefone é obrigatório para autenticação por WhatsApp`);
    }
  });
  return erros;
}
